// ©2024 Austin App House. All rights reserved.
import React, { useMemo } from 'react';
import getRatingColor from '../../../utils/getRatingColor';
import ContractorCertificatesChecks from '../ContractorCertificatesChecks';
import ContractorPreferedGroupsChecks from '../PreferedGroupsCheck';

const calcRating = (value, totalJobs = 0) => (
  value ? parseInt(value / (totalJobs + 20) * 100, 10) : 100
);

const SearchContractorCard = ({
  contractor,
  openInfo,
  inviteContractor,
  block,
  showBlockModalForCurrentContractor,
}) => {
  const ratings = useMemo(() => [
    { label: 'Soft Skills', value: calcRating(contractor.commRating, contractor.totalJobs) },
    { label: 'Work', value: calcRating(contractor.workRating, contractor.totalJobs) },
    { label: 'Deliverables', value: calcRating(contractor.deliverRating, contractor.totalJobs) },
    { label: 'Total', value: parseInt(contractor.currentRating || 100, 10) },
  ], [contractor]);

  const locationParts = [
    contractor.city?.label,
    contractor.state?.label,
    contractor.zipcode,
  ].filter(Boolean);

  return (
    <article className="work-order-card contractor-search-card">
      <div className="wo-main">
        <div className="wo-info">
          <div className="contractor-card-head">
            <div className="contractor-card-avatar">
              <img
                src={contractor.image || 'https://www.w3schools.com/css/img_avatar.png'}
                alt=""
              />
              {contractor.groups?.length ? (
                <span className="contractor-card-preferred" title="Preferred groups">
                  <ContractorPreferedGroupsChecks />
                </span>
              ) : null}
            </div>
            <div className="contractor-card-head-text">
              <button
                type="button"
                className="wo-title contractor-card-name"
                onClick={() => openInfo(contractor)}
              >
                {contractor.name}
              </button>
              <div className="wo-meta">
                <p>
                  <span className="wo-label">Contractor ID:</span>
                  {' '}
                  {contractor.unique_id}
                </p>
                {contractor.address ? (
                  <p>
                    <span className="wo-label">Address:</span>
                    {' '}
                    {contractor.address}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="wo-footer-meta">
            {locationParts.length ? (
              <span className="wo-location">{locationParts.join(', ')}</span>
            ) : null}
            {(contractor.distance || contractor.distance === 0) ? (
              <span className="wo-distance">
                {`${contractor.distance.toFixed(2)} Miles`}
              </span>
            ) : null}
          </div>
        </div>

        <div className="wo-dates contractor-card-section">
          <h4 className="wo-assigned-title">Certifications</h4>
          {contractor.certificates?.length ? (
            <ContractorCertificatesChecks certificates={contractor.certificates} />
          ) : (
            <p className="contractor-card-empty">No certifications</p>
          )}
        </div>

        <div className="wo-assigned contractor-card-section">
          <h4 className="wo-assigned-title">Contact</h4>
          {contractor.phone ? (
            <p>
              <span className="wo-label">Phone:</span>
              {' '}
              {contractor.phone}
            </p>
          ) : null}
          {contractor.email ? (
            <p>
              <span className="wo-label">Email:</span>
              {' '}
              {contractor.email}
            </p>
          ) : null}
        </div>

        <div className="wo-pricing contractor-card-ratings">
          {ratings.map(({ label, value }) => (
            <span
              key={label}
              className="contractor-rating-pill"
              style={{ color: getRatingColor(value) }}
              title={label}
            >
              <span className="contractor-rating-pill__label">{label}</span>
              <span className="contractor-rating-pill__value">{value}%</span>
            </span>
          ))}
        </div>
      </div>

      <div className="wo-actions">
        <button
          type="button"
          className="wo-btn wo-btn--details"
          onClick={() => openInfo(contractor)}
        >
          Details
        </button>
        {block ? (
          <button
            type="button"
            className="wo-btn wo-btn--blocked"
            onClick={() => showBlockModalForCurrentContractor(contractor)}
          >
            Blocked
          </button>
        ) : (
          <button
            type="button"
            className="wo-btn wo-btn--confirm"
            onClick={() => inviteContractor(contractor)}
          >
            Invite
          </button>
        )}
      </div>
    </article>
  );
};

export default SearchContractorCard;
